import { FC, Fragment, useCallback } from 'react';
import {
  Button,
  ButtonGroup,
  Dropdown,
  Icon,
  IconButton,
  Image,
  logos,
  Menu,
  MenuItem,
  Spacer,
  Stack,
  TableCell,
  Tag,
  TextBody,
  TextOverline,
} from '@pypestream/design-system';
import { TeamName } from './team-name';
import { RenderViaTeamDetails } from './render-via-team-details';
import './permissions-table.scss';
import {
  HandleAddTeam,
  HandleOrgCollapse,
  HandleRemoveTeam,
  HandleUpdateManagerRole,
  ShortOrgType,
} from './permissions-table.types';

export type OrgCellProps = {
  org: ShortOrgType;
  canManageAccess: boolean;
  isAddingTeamToOrg: boolean;
  isSavingDetails: boolean;
  handleOrgCollapse: HandleOrgCollapse;
  handleUpdateManagerRole: HandleUpdateManagerRole;
  handleRemoveTeam?: HandleRemoveTeam;
  handleAddTeam?: HandleAddTeam;
};

export const OrgCell: FC<OrgCellProps> = ({
  org,
  canManageAccess,
  isAddingTeamToOrg,
  isSavingDetails,
  handleUpdateManagerRole,
  handleOrgCollapse,
  handleRemoveTeam,
  handleAddTeam,
}) => {
  const renderAccountRolesDropdown = useCallback(
    ({
      canSelectMoreRole,
      roleSelectorId,
      isAddRole,
    }: {
      canSelectMoreRole: boolean;
      roleSelectorId: string;
      isAddRole: boolean;
    }) => {
      return (
        <>
          {(canSelectMoreRole && (
            <div>
              {!isAddRole && <Icon name="chevron-down"></Icon>}
              <Dropdown trigger={roleSelectorId} placement="bottom-start">
                <Menu>
                  {org.availableAccountRoles?.map((availableAccountRole) => (
                    <MenuItem
                      key={availableAccountRole.role.id}
                      onClick={() => {
                        handleUpdateManagerRole({
                          orgId: org.id || '',
                          role: availableAccountRole,
                          operation: isAddRole ? 'add' : 'update',
                        });
                      }}
                      disabled={isSavingDetails}
                    >
                      {availableAccountRole.role.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Dropdown>
            </div>
          )) ||
            null}
        </>
      );
    },
    [
      org.availableAccountRoles,
      org.id,
      isSavingDetails,
      handleUpdateManagerRole,
    ]
  );

  const hasDirectAccountRoleAssigned = org.accountRoles?.find(
    (role) => !role.teams
  );

  return (
    <TableCell
      rowspan={org.isCollapsed ? 1 : org.orgRowSpan}
      className="c-permissions-table--org-column"
    >
      <Stack alignItems="start" gutter="2xsmall" direction="column">
        <Stack alignItems="center" gutter="2xsmall">
          <IconButton
            name={org.isCollapsed ? 'chevron-down' : 'chevron-up'}
            size="xsmall"
            onClick={(e) => {
              e.stopPropagation();
              handleOrgCollapse({
                orgId: org.id || '',
                collapse: !org.isCollapsed,
              });
            }}
          ></IconButton>
          {org.isPrimaryAccount && (
            <Icon name="home" size="xsmall" weight="medium" color="gray"></Icon>
          )}
          <TextBody variant="secondary">{org.name}</TextBody>
        </Stack>
        {org.parentAccount &&
          // Ensure that Pypestream is not displayed as the parent name when rendering other organizations as PrimaryAccount
          (org.parentAccount.parentId || !org.isPrimaryAccount) && (
            <TextBody
              variant="tertiary"
              i18nKey="manager/common:permissionAndAccessTable.orgParent"
            >
              Parent: {org.parentAccount?.name}
            </TextBody>
          )}
      </Stack>
      {!org.isCollapsed && (
        <>
          {(!!org.assignedTeams || !!org.availableTeams) && (
            <>
              <Spacer size="large"></Spacer>
              <Stack alignItems="center" gutter="xsmall">
                <Icon
                  name="teams"
                  size="xsmall"
                  weight="medium"
                  color="gray"
                ></Icon>
                <TextOverline>Teams</TextOverline>
              </Stack>
              <Spacer size="medium"></Spacer>
            </>
          )}
          {(org.assignedTeams && org.assignedTeams?.length > 0 && (
            <>
              <Stack direction="column">
                {org.assignedTeams?.map((team) => (
                  <Tag
                    minimal
                    removable={canManageAccess}
                    new-variant="outlined"
                    key={`${org.id}-${team.team.id}`}
                    onRemove={() => {
                      handleRemoveTeam?.({ orgId: org.id || '', team });
                    }}
                    disabled={isSavingDetails}
                  >
                    <Icon name="teams"></Icon>
                    <TeamName team={team} />
                  </Tag>
                ))}
              </Stack>
              <Spacer size="small"></Spacer>
            </>
          )) ||
            null}
          {(canManageAccess &&
            org.availableTeams &&
            org.availableTeams?.length > 0 && (
              <Fragment key={`org-${org.id}-teams-section`}>
                <Button
                  variant="ghost"
                  size="small"
                  id={`org-${org.id}-team-selector`}
                  i18nKey="manager/common:permissionAndAccessTable.addTeam"
                  disabled={isAddingTeamToOrg || isSavingDetails}
                >
                  <Icon slot="prefix" name="add"></Icon>
                  Add Team
                </Button>
                <Dropdown
                  trigger={`org-${org.id}-team-selector`}
                  placement="bottom-start"
                  closeOnClickOutside
                >
                  <Menu>
                    {org.availableTeams?.map((team) => (
                      <MenuItem
                        key={team.id}
                        onClick={() => {
                          handleAddTeam?.({ orgId: org.id || '', team });
                        }}
                      >
                        {team.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Dropdown>
              </Fragment>
            )) ||
            (org.assignedTeams?.length == 0 && (
              <TextBody
                variant="secondary"
                size="small"
                i18nKey="manager/common:permissionAndAccessTable.teamEmptyState"
              >
                No teams found.
              </TextBody>
            )) ||
            null}
          {isAddingTeamToOrg && (
            <TextBody variant="tertiary" size="small">
              Adding team to org
            </TextBody>
          )}
          <Spacer size="xlarge"></Spacer>
          <Stack alignItems="center" gutter="xsmall">
            <Image width="16" height="16" src={logos.manager}></Image>
            <TextOverline>Manager</TextOverline>
          </Stack>
          <Spacer></Spacer>
          <Stack direction="column">
            {org.accountRoles &&
              org.accountRoles?.map((role, index) => {
                const isTeamInheritedRole =
                  role.teams && role?.teams.length > 0;
                const roleSelectorId = `${org.id}-${role.role.id}-${
                  isTeamInheritedRole ? 'inherited-role' : 'direct-role'
                }`;
                const canSelectMoreRole =
                  canManageAccess &&
                  !isTeamInheritedRole &&
                  org.isPrimaryAccount &&
                  (org.availableAccountRoles || [])?.length > 0;
                return (
                  <Tag
                    minimal
                    new-variant="outlined"
                    key={roleSelectorId}
                    removable={
                      org.isPrimaryAccount &&
                      !isTeamInheritedRole &&
                      canManageAccess
                    }
                    {...(canManageAccess &&
                      canSelectMoreRole && {
                        id: roleSelectorId,
                        interactive: !!canSelectMoreRole && !isSavingDetails,
                      })}
                    onRemove={() => {
                      handleUpdateManagerRole({
                        orgId: org.id || '',
                        role,
                        operation: 'remove',
                      });
                    }}
                    disabled={isSavingDetails}
                  >
                    <Icon name={isTeamInheritedRole ? 'teams' : 'users'}></Icon>
                    {role.role.name}
                    {isTeamInheritedRole && (
                      <RenderViaTeamDetails teams={role.teams || []} />
                    )}
                    {renderAccountRolesDropdown({
                      canSelectMoreRole,
                      roleSelectorId,
                      isAddRole: false,
                    })}
                  </Tag>
                );
              })}
            {((org.accountRoles || []).length === 0 ||
              !hasDirectAccountRoleAssigned) && (
              <ButtonGroup>
                <Tag
                  minimal
                  new-variant="outlined"
                  i18nKey="manager/common:permissionAndAccessTable.roles.viewOnly"
                >
                  <Icon name="users"></Icon>
                  View Only
                </Tag>
                {org.isPrimaryAccount &&
                  !hasDirectAccountRoleAssigned &&
                  canManageAccess && (
                    <>
                      <IconButton
                        name="add"
                        id={`${org.id}-add-account-role`}
                        disabled={isSavingDetails}
                      />
                      {renderAccountRolesDropdown({
                        canSelectMoreRole:
                          org.isPrimaryAccount &&
                          (org.availableAccountRoles || [])?.length > 0,
                        roleSelectorId: `${org.id}-add-account-role`,
                        isAddRole: true,
                      })}
                    </>
                  )}
              </ButtonGroup>
            )}
          </Stack>
        </>
      )}
    </TableCell>
  );
};
